/**
 * Connected accounts — Settings → Security, on the web portals and the phone.
 * The Clerk work is `useConnectedAccounts` (@tonnie/sdk); this is the screen's
 * side: the words for each outcome (never Clerk's English), a report for
 * anything unexpected, and which connection is being removed.
 *
 * What differs per client is handed in: how it tells an outcome (`notify`),
 * where the provider sends the person back (`returnUrl`), and — on the phone,
 * which cannot leave for the consent screen — the in-app `consent` sheet.
 */
import { SOCIAL_PROVIDER_NAME, type SocialProvider } from '@tonnie/foundation';
import { useAuthErrorMessage, useTranslation } from '@tonnie/locale';
import { useErrorCapture } from '@tonnie/observability';
import { useConnectedAccounts, type ConnectedAccount, type ConsentSession } from '@tonnie/sdk';
import { useCallback, useEffect, useState } from 'react';

import type { AccountNotifier } from './notifier';

export function useConnectedAccountsCard({
  notify,
  returnUrl,
  consent,
}: {
  notify: AccountNotifier;
  /** Where the provider sends the person back after its consent screen. */
  returnUrl: string;
  /** The in-app consent sheet, for a client that cannot leave the page. */
  consent?: ConsentSession;
}) {
  const { t } = useTranslation();
  const describeAuthError = useAuthErrorMessage();
  const { captureException } = useErrorCapture();
  const { accounts, providers, connect, disconnect, returned, clearReturned } =
    useConnectedAccounts({ consent });
  const [busy, setBusy] = useState<SocialProvider | null>(null);
  const [removing, setRemoving] = useState<ConnectedAccount | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fail = useCallback(
    (err: unknown, fallback: string, action: string) => {
      const failure = describeAuthError(err, fallback);
      if (!failure.expected) {
        captureException(err instanceof Error ? err : new Error(String(err)), {
          component: 'connected-accounts',
          action,
        });
      }
      setError(failure.message);
    },
    [describeAuthError, captureException]
  );

  // Back from the provider: say how it went.
  useEffect(() => {
    if (!returned) return;
    const provider = SOCIAL_PROVIDER_NAME[returned.provider];
    if (returned.result === 'connected') {
      notify.success(t('auth.connected.connected', { provider }));
    } else {
      fail(returned.error, t('auth.connected.failed', { provider }), 'return');
    }
    clearReturned();
  }, [returned, clearReturned, fail, t, notify]);

  const onConnect = useCallback(
    async (provider: SocialProvider) => {
      setError(null);
      setBusy(provider);
      const result = await connect(provider, returnUrl);
      const name = SOCIAL_PROVIDER_NAME[provider];
      switch (result.outcome) {
        case 'redirecting':
          // The web is leaving for the provider's consent screen — stay busy.
          return;
        case 'done':
          setBusy(null);
          notify.success(t('auth.connected.connected', { provider: name }));
          return;
        case 'cancelled':
          setBusy(null);
          return;
        case 'failed':
          setBusy(null);
          fail(result.error, t('auth.connected.failed', { provider: name }), 'connect');
          return;
        default: {
          const exhaustive: never = result;
          throw new Error(`Unhandled outcome: ${JSON.stringify(exhaustive)}`);
        }
      }
    },
    [connect, returnUrl, fail, t, notify]
  );

  const onConfirmDisconnect = useCallback(async () => {
    if (!removing) return;
    const provider = SOCIAL_PROVIDER_NAME[removing.provider];
    setError(null);
    setBusy(removing.provider);
    const result = await disconnect(removing.id);
    setBusy(null);
    setRemoving(null);
    switch (result.outcome) {
      case 'done':
        notify.success(t('auth.connected.disconnected', { provider }));
        return;
      case 'cancelled':
        return;
      case 'failed':
        fail(result.error, t('auth.connected.disconnectFailed', { provider }), 'disconnect');
        return;
      default: {
        const exhaustive: never = result;
        throw new Error(`Unhandled outcome: ${JSON.stringify(exhaustive)}`);
      }
    }
  }, [removing, disconnect, fail, t, notify]);

  return {
    accounts,
    providers,
    busy,
    error,
    removing,
    askToDisconnect: setRemoving,
    cancelDisconnect: useCallback(() => setRemoving(null), []),
    onConnect,
    onConfirmDisconnect,
  };
}
